/**
 * @file ComplianceStats.jsx
 * @description ComplianceStats component for displaying and managing compliance stats.
 * @component ComplianceStats
 * @exports ComplianceStats - Main component for displaying and managing compliance stats.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import {
    COMPLIANCE_STATS_TABS,
    COMPLIANCE_STATS_TABLE_COLUMN_KEYS,
    COMPLIANCE_STATS_ERRORS,
    COMPLIANCE_STATS_MESSAGES,
    formatDateTime,
} from "../../../common/constants/dashboardModule/complianceStatsConstants.js";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { fetchComplianceData } from "../../../services/dashboardModule/dashboardServices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import ComplianceStatInfo from "./ComplianceStatInfo.jsx";
import ComplianceDescriptionPercentBarChart from "./ComplianceChart.jsx";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const PINK   = "#D4537E";
const AMBER  = "#BA7517";

// ── Column config ──────────────────────────────────────────────────
const COLS = [
    { key: COMPLIANCE_STATS_TABLE_COLUMN_KEYS.VIEW,           label: "",           width: 36,  align: "center" },
    { key: COMPLIANCE_STATS_TABLE_COLUMN_KEYS.GROUP,          label: "Group",      width: 180, align: "left"   },
    { key: COMPLIANCE_STATS_TABLE_COLUMN_KEYS.RULE_ID,        label: "Rule ID",    width: 80,  align: "left"   },
    { key: COMPLIANCE_STATS_TABLE_COLUMN_KEYS.DESCRIPTION,    label: "Description",width: 240, align: "left"   },
    { key: COMPLIANCE_STATS_TABLE_COLUMN_KEYS.YES_PERCENTAGE, label: "Yes %",      width: 70,  align: "right"  },
    { key: COMPLIANCE_STATS_TABLE_COLUMN_KEYS.NO_PERCENTAGE,  label: "No %",       width: 70,  align: "right"  },
    { key: COMPLIANCE_STATS_TABLE_COLUMN_KEYS.TOTAL,          label: "Total",      width: 65,  align: "right"  },
    { key: COMPLIANCE_STATS_TABLE_COLUMN_KEYS.YES,            label: "Yes",        width: 60,  align: "right"  },
    { key: COMPLIANCE_STATS_TABLE_COLUMN_KEYS.NO,             label: "No",         width: 60,  align: "right"  },
];

const NON_SORTABLE = new Set([COMPLIANCE_STATS_TABLE_COLUMN_KEYS.VIEW]);
const GRID_COLS    = COLS.map(c => `${c.width}px`).join(" ");
const TABLE_WIDTH  = COLS.reduce((s, c) => s + c.width, 0) + 64;
const ROWS_PER_PAGE = 25;

// ── Helpers ────────────────────────────────────────────────────────
function SectionTitle({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
        }}>
            {children}
        </Typography>
    );
}

function PctCell({ value, isYes }) {
    const num = parseFloat(value) || 0;
    if (num === 0) return <Box sx={{ color: "text.disabled" }}>—</Box>;
    return (
        <Box component="span" sx={{
            display: "inline-block",
            px: "7px", py: "1px",
            borderRadius: "20px",
            fontSize: 11, fontWeight: 500,
            bgcolor: isYes ? "rgba(29,158,117,0.09)" : "rgba(212,83,126,0.09)",
            color:   isYes ? GREEN : PINK,
            border:  `0.5px solid ${isYes ? "rgba(29,158,117,0.25)" : "rgba(212,83,126,0.25)"}`,
        }}>
            {value}
        </Box>
    );
}

// ── Component ──────────────────────────────────────────────────────
const ComplianceStats = () => {
    const today          = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const [startDate,          setStartDate]          = useState(firstDayOfMonth);
    const [endDate,            setEndDate]            = useState(today);
    const [complianceData,     setComplianceData]     = useState([]);
    const [loadingData,        setLoadingData]        = useState(false);
    const [error,              setError]              = useState("");
    const [viewComplianceStat, setViewComplianceStat] = useState(null);
    const [complianceType,     setComplianceType]     = useState(COMPLIANCE_STATS_TABS.TEXTS);
    const [page,               setPage]               = useState(0);
    const [sortKey,            setSortKey]            = useState(null);
    const [sortDir,            setSortDir]            = useState("asc");

    useEffect(() => {
        load(firstDayOfMonth, today, COMPLIANCE_STATS_TABS.TEXTS);
    }, []);

    const load = async (start, end, type) => {
        try {
            setLoadingData(true); setError(""); setPage(0);
            setComplianceData((await fetchComplianceData({
                startDate:      formatDateTime(start),
                endDate:        formatDateTime(end),
                complianceType: type,
            })) || []);
        } catch (e) {
            console.error(COMPLIANCE_STATS_ERRORS.FETCH_COMPLIANCE_STATS_ERROR, e);
            setError("Failed to load compliance data.");
        } finally {
            setLoadingData(false);
        }
    };

    const handleSortCol = (key) => {
        if (NON_SORTABLE.has(key)) return;
        if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
        setPage(0);
    };

    const sortedData = useMemo(() => {
        if (!sortKey) return complianceData;
        return [...complianceData].sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const an = Number(av), bn = Number(bv);
            const cmp = !isNaN(an) && !isNaN(bn)
                ? an - bn
                : String(av ?? "").localeCompare(String(bv ?? ""));
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [complianceData, sortKey, sortDir]);

    const pagedData  = useMemo(() =>
        sortedData.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
    , [sortedData, page]);

    const totalPages = Math.ceil(complianceData.length / ROWS_PER_PAGE);

    const kpis = useMemo(() => {
        const total   = complianceData.length;
        const sumYes  = complianceData.reduce((a, r) => a + (Number(r.Yes)   || 0), 0);
        const sumNo   = complianceData.reduce((a, r) => a + (Number(r.No)    || 0), 0);
        const sumAll  = complianceData.reduce((a, r) => a + (Number(r.Total) || 0), 0);
        const avgYesPct = total
            ? complianceData.reduce((a, r) => a + (parseFloat(r["Yes%"]) || 0), 0) / total
            : 0;
        return { total, sumYes, sumNo, sumAll, avgYesPct };
    }, [complianceData]);

    const gridSx = {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        width: `${TABLE_WIDTH}px`,
        px: 2,
    };

    if (viewComplianceStat) {
        return (
            <ComplianceStatInfo
                complianceRuleID={viewComplianceStat}
                complianceType={complianceType}
                onBack={() => setViewComplianceStat(null)}
            />
        );
    }

    return (
        <Box sx={{ p: 0 }}>
            {/* ── Filters ── */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 2,
                alignItems: "flex-end",
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <DatePickerComponent
                    label={STRINGS.START_DATE}
                    date={startDate}
                    onDateChange={setStartDate}
                    marginRight={0}
                />
                <DatePickerComponent
                    label={STRINGS.END_DATE}
                    date={endDate}
                    onDateChange={d => { if (d >= startDate) setEndDate(d); }}
                    marginRight={0}
                />
                <Box
                    onClick={() => load(startDate, endDate, complianceType)}
                    sx={{
                        px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                        cursor: "pointer", userSelect: "none",
                        background: BLUE, color: "#fff", fontWeight: 500,
                        alignSelf: "flex-end", mb: "1px",
                        "&:hover": { background: "#2a6db5" },
                    }}
                >
                    Search
                </Box>

                {/* Channel toggle */}
                <Box sx={{
                    display: "flex", ml: "auto",
                    border: "0.5px solid", borderColor: "divider",
                    borderRadius: "20px", overflow: "hidden",
                    alignSelf: "flex-end",
                }}>
                    {[COMPLIANCE_STATS_TABS.TEXTS, COMPLIANCE_STATS_TABS.EMAILS].map(ch => (
                        <Box key={ch} onClick={() => {
                            setComplianceType(ch);
                            load(startDate, endDate, ch);
                        }} sx={{
                            px: "14px", py: "5px", fontSize: 13,
                            fontWeight:  complianceType === ch ? 500 : 400,
                            cursor: "pointer", userSelect: "none",
                            background:  complianceType === ch ? BLUE : "transparent",
                            color:       complianceType === ch ? "#fff" : "text.secondary",
                            transition:  "all .15s",
                        }}>
                            {ch}
                        </Box>
                    ))}
                </Box>
            </Box>

            {error      && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loadingData && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                    <CircularProgress size={20} /> Loading…
                </Box>
            )}

            {!loadingData && complianceData.length === 0 && !error && (
                <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                    {COMPLIANCE_STATS_MESSAGES.NO_COMPLIANCE_STATS}
                </Typography>
            )}

            {!loadingData && complianceData.length > 0 && (
                <>
                    {/* ── KPIs ── */}
                    <SectionTitle>Summary</SectionTitle>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                        {[
                            { label: "Rules",       value: kpis.total.toLocaleString(),          color: BLUE  },
                            { label: "Total checks",value: kpis.sumAll.toLocaleString(),          color: BLUE  },
                            { label: "Yes",         value: kpis.sumYes.toLocaleString(),          color: GREEN },
                            { label: "No",          value: kpis.sumNo.toLocaleString(),           color: PINK  },
                            { label: "Avg Yes %",   value: `${kpis.avgYesPct.toFixed(1)}%`,       color: AMBER },
                        ].map(k => (
                            <Box key={k.label} sx={{
                                background: "#f7f7f7", borderRadius: 2,
                                p: "10px 14px", flex: 1, minWidth: 0,
                            }}>
                                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                                    {k.label}
                                </Typography>
                                <Typography sx={{ fontSize: 22, fontWeight: 500, color: k.color, lineHeight: 1.2 }}>
                                    {k.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* ── Chart ── */}
                    <ComplianceDescriptionPercentBarChart
                        complianceStats={pagedData}
                        title="Compliance %"
                    />

                    {/* ── Table ── */}
                    <SectionTitle>Detail</SectionTitle>

                    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto" }}>
                        {/* Header row */}
                        <Box sx={{
                            ...gridSx, py: 1,
                            background: "#f7f7f7",
                            borderBottom: "0.5px solid", borderColor: "divider",
                            fontSize: 11, fontWeight: 500,
                            textTransform: "uppercase", letterSpacing: "0.05em",
                        }}>
                            {COLS.map(col => (
                                <Box
                                    key={col.key}
                                    onClick={() => handleSortCol(col.key)}
                                    sx={{
                                        textAlign: col.align,
                                        cursor: NON_SORTABLE.has(col.key) ? "default" : "pointer",
                                        userSelect: "none",
                                        display: "flex", gap: 0.5, alignItems: "center",
                                        justifyContent:
                                            col.align === "right"  ? "flex-end"  :
                                            col.align === "center" ? "center"    : "flex-start",
                                        color: sortKey === col.key ? BLUE : "text.secondary",
                                        "&:hover": { color: NON_SORTABLE.has(col.key) ? "text.secondary" : BLUE },
                                    }}
                                >
                                    {col.label}
                                    {sortKey === col.key && (
                                        <span style={{ fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        {/* Data rows */}
                        {pagedData.map((row, i) => (
                            <Box key={row.RuleID ?? i} sx={{
                                ...gridSx, py: "7px", fontSize: 12,
                                borderBottom: "0.5px solid", borderColor: "divider",
                                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                                "&:hover": { background: "rgba(55,138,221,0.06)" },
                            }}>
                                {COLS.map(col => (
                                    <Box key={col.key} sx={{
                                        display: "flex", alignItems: "center",
                                        justifyContent:
                                            col.align === "right"  ? "flex-end"  :
                                            col.align === "center" ? "center"    : "flex-start",
                                        ...(col.key === COMPLIANCE_STATS_TABLE_COLUMN_KEYS.RULE_ID && {
                                            fontFamily: "monospace", color: "text.secondary", fontSize: 11,
                                        }),
                                        ...(col.key === COMPLIANCE_STATS_TABLE_COLUMN_KEYS.DESCRIPTION && {
                                            overflow: "hidden", textOverflow: "ellipsis",
                                            whiteSpace: "nowrap", pr: 2, color: "text.primary",
                                        }),
                                        ...(col.key === COMPLIANCE_STATS_TABLE_COLUMN_KEYS.GROUP && {
                                            color: "text.secondary",
                                        }),
                                        ...(col.key === COMPLIANCE_STATS_TABLE_COLUMN_KEYS.TOTAL && {
                                            fontWeight: 500,
                                        }),
                                        ...(col.key === COMPLIANCE_STATS_TABLE_COLUMN_KEYS.YES && {
                                            color: GREEN, fontWeight: 500,
                                        }),
                                        ...(col.key === COMPLIANCE_STATS_TABLE_COLUMN_KEYS.NO && {
                                            color: PINK, fontWeight: 500,
                                        }),
                                    }}>
                                        {col.key === COMPLIANCE_STATS_TABLE_COLUMN_KEYS.VIEW ? (
                                            <Box
                                                onClick={() => setViewComplianceStat(row.RuleID)}
                                                sx={{
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    width: 24, height: 24, borderRadius: "6px",
                                                    cursor: "pointer",
                                                    color: "text.disabled",
                                                    "&:hover": { color: BLUE, background: "rgba(55,138,221,0.1)" },
                                                }}
                                            >
                                                <Visibility sx={{ fontSize: 14 }} />
                                            </Box>
                                        ) : col.key === COMPLIANCE_STATS_TABLE_COLUMN_KEYS.YES_PERCENTAGE ? (
                                            <PctCell value={row["Yes%"]} isYes={true} />
                                        ) : col.key === COMPLIANCE_STATS_TABLE_COLUMN_KEYS.NO_PERCENTAGE ? (
                                            <PctCell value={row["No%"]} isYes={false} />
                                        ) : (
                                            row[col.key] ?? "—"
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <Box sx={{
                            display: "flex", justifyContent: "space-between",
                            alignItems: "center", mt: 1, px: 1,
                        }}>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                Showing {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, complianceData.length)} of {complianceData.length}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 12, color: "text.secondary" }}>
                                <Box onClick={() => setPage(p => Math.max(0, p - 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page === 0 ? "default" : "pointer",
                                    opacity: page === 0 ? 0.4 : 1,
                                    "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                                }}>‹</Box>
                                <span>Page {page + 1} of {totalPages}</span>
                                <Box onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page >= totalPages - 1 ? "default" : "pointer",
                                    opacity: page >= totalPages - 1 ? 0.4 : 1,
                                    "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                                }}>›</Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ComplianceStats;